import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useFirebase } from './firebaseContext';
import { onAuthStateChanged, signOut, sendPasswordResetEmail } from 'firebase/auth';

export const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
    const { auth } = useFirebase();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setUser(user);
            setLoading(false);
        });

        return unsubscribe;
    }, [auth]);

    const logout = async () => {
        try {
            await signOut(auth);
            setUser(null);
        } catch (error) {
            console.error('Error logging out:', error);
        }
    };

    const resetPassword = useCallback(async (rawEmail) => {
        const email = rawEmail?.trim();

        if (!email) {
            const error = new Error('Email is required');
            error.code = 'auth/missing-email';
            throw error;
        }

        try {
            await sendPasswordResetEmail(auth, email);
        } catch (error) {
            console.error('Error sending password reset email:', error);
            throw error;
        }
    }, [auth]);

    const value = useMemo(() => ({
        user,
        loading,
        logout,
        signOut: logout,
        resetPassword,
    }), [user, loading, logout, resetPassword]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
